﻿import { ApiCaller, FullfilledApiResponse } from 'apis/ApiCaller';
import { PaginatedResult } from 'apis/TransactionApi';
import { Transaction } from 'apis/TransactionApi';
import { Instructor } from 'apis/InstructorApi';
import type { Student } from 'apis/StudentApi';

/**
 * Valid sort column names for StudentGroup GetAllPaged when type is 'Partner', 'User', or 'TestingCenter'.
 */
export type StudentGroupPartnerSortField =
    | 'Id'
    | 'Name'
    | 'UserId'
    | 'UserFirstName'
    | 'UserLastName'
    | 'PartnerId'
    | 'PartnerName'
    | 'Description'
    | 'DateCreated'
    | 'Control'
    | 'MaxStudents'
    | 'Waitlist'
    | 'Instructors'
    | 'StudentCount'
    | 'AssignmentCount'
    | 'active'
    | 'Archived'
    | 'ExpirationDate'
    | 'LeastLicenseExpirationDate';

/**
 * Valid sort column names for StudentGroup GetAllPaged when type is 'Student'.
 */
export type StudentGroupStudentSortField =
    | 'Id'
    | 'Name'
    | 'Description'
    | 'PartnerId'
    | 'PartnerName'
    | 'MaxStudents'
    | 'Waitlist'
    | 'Control'
    | 'DateCreated'
    | 'StudentDateAdded'
    | 'StudentWaitListed'
    | 'StudentWaitlistOrder'
    | 'StudentNotes'
    | 'StudentScore'
    | 'StudentStatusId'
    | 'StudentStatusName'
    | 'StudentBalance'
    | 'StudentInstructorId'
    | 'StudentInstructorFirstName'
    | 'StudentInstructorLastName'
    | 'StudentCount'
    | 'AssignmentCount'
    | 'ExpirationDate';

interface Partner {
    Id: number;
    Name: string;
}

interface StudentGroupStudent {
    Balance: number;
    DateAdded: string;
    DateAddedString: string;
    Instructor: Instructor;
    Notes: string;
    Score: number;
    Status: number;
    StatusName: string;
    WaitListed: string;
    WaitlistOrder: number;
}

export interface StudentGroup {
    Id: number;
    Name: string;
}

export interface StudentGroupWithDetails extends StudentGroup {
    Active: boolean;
    Archived?: boolean;
    Control?: string;
    DateCreate: string;
    Description?: string;
    ExpirationDate?: string | null;
    Instructors: Instructor[];
    InstructorString?: string;
    LeastLicenseExpirationDate ?: Date;
    MaxStudents?: number;
    Partner?: Partner;
    StudentCount: number;
    StudentGroupStudents?: StudentGroupStudent[];
    TransactionCount: number;
    UserId: number;
    Waitlist?: number;
}

type GetAllType = 'User' | 'TestingCenter' | 'Student' | 'Partner';

export class StudentGroupApi {

    private static apiCaller = new ApiCaller('StudentGroup');

    public static GetAll(type: 'Student', studentId: number, objectId?: number): Promise<StudentGroupWithDetails[]>;

    public static GetAll(type: 'User' | 'TestingCenter' | 'Partner', studentId?: number, objectId?: number): Promise<StudentGroupWithDetails[]>;

    public static GetAll(type: GetAllType, studentId?: number, objectId?: number): Promise<StudentGroupWithDetails[]> {

        return this.apiCaller.GET<StudentGroupWithDetails[]>('GetAll', { objectId: objectId ?? null, studentId: studentId ?? null, type });

    }

    public static GetAllPaged(
        page = 1,
        pageSize = 25,
        sortField: StudentGroupPartnerSortField | StudentGroupStudentSortField | '' = '',
        sortDir = '',
        searchTerm = '',
        showArchived = false,
        showMyClassrooms = false,
        type: GetAllType = 'Partner',
        studentId?: number
    ): Promise<PaginatedResult<StudentGroupWithDetails>> {

        return this.apiCaller.GET<PaginatedResult<StudentGroupWithDetails>>('GetAllPaged', {
            page,
            pageSize,
            searchTerm,
            showArchived,
            showMyClassrooms,
            sortDir,
            sortField,
            studentId: studentId ?? null,
            type
        });

    }

    public static Get(groupId: number) {

        return this.apiCaller.GET<StudentGroupWithDetails>('Get', { groupId });

    }

    public static GetAllStudents(groupId: number, partnerId: number | null = null) {

        return this.apiCaller.GET<Student[]>('GetAllStudents', { groupId, partnerId });

    }

    public static GetAllStudentsForGroups(groupIds: number[], partnerId: number | null = null) {

        return this.apiCaller.POST<Student[]>('GetAllStudentsForGroups', { groupIds: groupIds.join(','), partnerId });

    }

    public static GetGroupsByTransactionId(
        transactionId: number,
        cultureId = 1,
        partnerId: number | null = null
    ): Promise<StudentGroupWithDetails[]> {

        return this.apiCaller.GET<StudentGroupWithDetails[]>('GetGroupsByTransactionId', { cultureId, partnerId, transactionId });

    }

    public static GetTransactionsFromGroup(groupId: number, partnerId: number | null = null) {

        return this.apiCaller.GET<Transaction[]>('GetTransactionsFromGroup', { groupId, partnerId });

    }

    public static DeleteTransactionFromGroup(transactionIds: number[], groupIds: number[], partnerId: number | null = null): Promise<void> {

        return this.apiCaller.DELETE('DeleteTransactionFromGroup', { groupIds, partnerId, transactionIds });

    }

    public static GetAllTransactions(groupId: number) {

        return this.apiCaller.GET<Transaction[]>('GetAllTransactions', { groupId });

    }

    public static AddStudentToGroup(groupIds: number[], studentIds: number[]) {

        return this.apiCaller.POST<void>('AddStudentToGroup', { groupIds, studentIds });

    }

    public static DeleteStudentFromGroup(groupId: number, studentIds: number[]) {

        return this.apiCaller.DELETE<void>('DeleteStudentFromGroup', { groupId, studentIds });

    }

    public static CreateEditStudentGroup(
        groupId: number,
        groupName: string,
        description: string,
        maxStudents: number,
        active: boolean,
        classroomCode: string | null = null,
        partnerId: number | null = null
    ) {

        return this.apiCaller.POST<FullfilledApiResponse>('CreateEditStudentGroup', {
            active,
            classroomCode,
            description,
            groupId,
            groupName,
            maxStudents,
            partnerId
        });

    }

    public static AddEditInstructorByGroup(groupIds: number[], instructorIds: number[], manageFullGroups: boolean) {

        return this.apiCaller.POST<void>('AddEditInstructorByGroup', { groupIds, instructorIds, manageFullGroups });

    }

    public static RemoveInstructorFromGroup(groupIds: number[], instructorIds: number[]) {

        return this.apiCaller.DELETE<void>('RemoveInstructorFromGroup', { groupIds, instructorIds });

    }

    public static EditStudentGroupIdentifier(groupId: number, controlOverride: string): Promise<FullfilledApiResponse> {

        return this.apiCaller.PUT<FullfilledApiResponse>('EditStudentGroupIdentifier', { controlOverride, groupId });

    }

    public static SetArchivedStatus(studentGroupId: number, archiveStatus: boolean): Promise<FullfilledApiResponse> {

        return this.apiCaller.PUT<FullfilledApiResponse>('SetArchivedStatus', { archiveStatus, studentGroupId });

    }

}
